import { motion, AnimatePresence } from "framer-motion";
import { X, Volume2, BookOpen, ArrowRight } from "lucide-react";
import type { DictionaryEntry } from "@/types/dictionary";

interface DictionaryPopupProps {
  entry: DictionaryEntry | null;
  isOpen: boolean;
  onClose: () => void;
}

export function DictionaryPopup({
  entry,
  isOpen,
  onClose,
}: DictionaryPopupProps) {
  if (!entry) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
            onClick={onClose}
          />

          {/* Popup - Mobile: Bottom Sheet, Desktop: Center Modal */}
          <motion.div
            initial={{ opacity: 0, y: "100%" }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed z-50
              /* Mobile: Bottom Sheet */
              inset-x-0 bottom-0
              w-full max-w-none
              rounded-t-2xl rounded-b-none

              /* Desktop: Center Modal */
              md:inset-auto md:left-1/2 md:top-1/2
              md:-translate-x-1/2 md:-translate-y-1/2
              md:w-[90vw] md:max-w-md
              md:rounded-2xl

              bg-background-secondary border-t md:border border-white/10 shadow-2xl overflow-hidden"
          >
            {/* Mobile Drag Handle */}
            <div className="md:hidden w-full flex justify-center pt-3 pb-1">
              <div className="w-12 h-1.5 bg-white/20 rounded-full" />
            </div>

            {/* Header */}
            <div className="bg-gradient-to-r from-accent-purple/20 to-accent-blue/20 p-4 border-b border-white/10">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  {/* Kelime ve çekim */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display text-2xl md:text-2xl text-white">
                      {entry.word}
                    </h3>
                    <button
                      className="p-1.5 hover:bg-white/10 rounded-full transition-colors group flex-shrink-0"
                      title="Telaffuz"
                    >
                      <Volume2 className="w-5 h-5 text-txt-muted group-hover:text-accent-blue transition-colors" />
                    </button>
                    {entry.word_in_context !== entry.word && (
                      <span className="text-txt-secondary text-sm">
                        → {entry.word_in_context}
                      </span>
                    )}
                  </div>

                  {/* Tür ve Seviye */}
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <span className="px-2 py-0.5 bg-white/10 rounded text-xs text-txt-secondary">
                      {getWordTypeLabel(entry.word_type)}
                    </span>
                    {entry.difficulty && (
                      <span
                        className={`px-2 py-0.5 rounded text-xs ${getDifficultyColor(
                          entry.difficulty
                        )}`}
                      >
                        {entry.difficulty}
                      </span>
                    )}
                  </div>
                </div>

                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors flex-shrink-0 ml-2"
                >
                  <X className="w-5 h-5 text-txt-secondary" />
                </button>
              </div>

              {/* Çekim bilgisi */}
              {entry.inflection && (
                <div className="mt-3 p-2 bg-white/5 rounded-lg">
                  <p className="text-sm text-txt-secondary">
                    <span className="text-accent-purple">
                      {entry.word_in_context}
                    </span>
                    {" = "}
                    <span className="text-white">
                      {entry.inflection.base_form || entry.word}
                    </span>
                    {entry.inflection.case && (
                      <span className="text-accent-blue">
                        {" "}
                        + {getCaseLabel(entry.inflection.case)}
                      </span>
                    )}
                    {entry.inflection.tense && (
                      <span className="text-accent-orange">
                        {" "}
                        ({getTenseLabel(entry.inflection.tense)},{" "}
                        {entry.inflection.person}. tekil)
                      </span>
                    )}
                    {entry.inflection.plural && (
                      <span className="text-accent-coral"> (çoğul)</span>
                    )}
                  </p>
                </div>
              )}
            </div>

            {/* Anlamlar */}
            <div className="p-4 max-h-[40vh] md:max-h-[50vh] overflow-y-auto">
              <div className="space-y-4">
                {entry.meanings.map((meaning, index) => (
                  <div key={index} className="space-y-2">
                    {/* Tanım */}
                    <div>
                      <p className="text-white font-medium">
                        {meaning.definition_tr}
                      </p>
                      {meaning.definition_en && (
                        <p className="text-txt-muted text-sm">
                          {meaning.definition_en}
                        </p>
                      )}
                    </div>

                    {/* Örnek cümle */}
                    <div className="p-3 bg-white/5 rounded-lg">
                      <p className="text-white font-transcript">
                        {meaning.example_fi}
                      </p>
                      <p className="text-accent-blue text-sm mt-1">
                        {meaning.example_tr}
                      </p>
                      {meaning.context && (
                        <p className="text-txt-muted text-xs mt-2 italic">
                          {meaning.context}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* İlişkili kelimeler */}
              {entry.related && (
                <div className="mt-6 pt-4 border-t border-white/10">
                  <h4 className="text-sm text-txt-secondary mb-3 flex items-center gap-2">
                    <BookOpen className="w-4 h-4" />
                    İlişkili Kelimeler
                  </h4>

                  <div className="space-y-2">
                    {entry.related.synonyms &&
                      entry.related.synonyms.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          <span className="text-xs text-txt-muted">
                            Eş anlamlı:
                          </span>
                          {entry.related.synonyms.map((word) => (
                            <span
                              key={word}
                              className="px-2 py-0.5 bg-green-500/10 text-green-400 rounded text-sm cursor-pointer hover:bg-green-500/20 transition-colors"
                            >
                              {word}
                            </span>
                          ))}
                        </div>
                      )}

                    {entry.related.antonyms &&
                      entry.related.antonyms.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          <span className="text-xs text-txt-muted">
                            Zıt anlamlı:
                          </span>
                          {entry.related.antonyms.map((word) => (
                            <span
                              key={word}
                              className="px-2 py-0.5 bg-red-500/10 text-red-400 rounded text-sm cursor-pointer hover:bg-red-500/20 transition-colors"
                            >
                              {word}
                            </span>
                          ))}
                        </div>
                      )}

                    {entry.related.compounds &&
                      entry.related.compounds.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          <span className="text-xs text-txt-muted">
                            Bileşik:
                          </span>
                          {entry.related.compounds.map((word) => (
                            <span
                              key={word}
                              className="px-2 py-0.5 bg-purple-500/10 text-purple-400 rounded text-sm cursor-pointer hover:bg-purple-500/20 transition-colors"
                            >
                              {word}
                            </span>
                          ))}
                        </div>
                      )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] md:pb-3 bg-white/5 border-t border-white/10 flex justify-between items-center flex-wrap gap-2">
              <span className="text-xs text-txt-muted">
                Sıklık: {"★".repeat(Math.min(entry.frequency || 5, 5))}
                {"☆".repeat(5 - Math.min(entry.frequency || 5, 5))}
              </span>
              <button className="text-sm text-accent-blue hover:text-white transition-colors flex items-center gap-1">
                Kelime listesine ekle
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// Yardımcı fonksiyonlar
function getWordTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    noun: "İsim",
    verb: "Fiil",
    adjective: "Sıfat",
    adverb: "Zarf",
    pronoun: "Zamir",
    preposition: "Edat",
    postposition: "Son edat",
    conjunction: "Bağlaç",
    interjection: "Ünlem",
    numeral: "Sayı",
    particle: "Parçacık",
    prefix: "Önek",
    proper_noun: "Özel isim",
    unknown: "Bilinmiyor",
  };
  return labels[type] || type;
}

function getCaseLabel(caseType: string): string {
  const labels: Record<string, string> = {
    nominative: "yalın hal",
    genitive: "iyelik hali (-n)",
    partitive: "kısmi hal (-a/-ä)",
    inessive: "içinde (-ssa/-ssä)",
    elative: "içinden (-sta/-stä)",
    illative: "içine",
    adessive: "üzerinde (-lla/-llä)",
    ablative: "üzerinden (-lta/-ltä)",
    allative: "üzerine (-lle)",
    essive: "olarak (-na/-nä)",
    translative: "dönüşüm (-ksi)",
    abessive: "olmaksızın (-tta/-ttä)",
    comitative: "birlikte (-ne)",
    instructive: "araç (-n)",
  };
  return labels[caseType] || caseType;
}

function getTenseLabel(tense: string): string {
  const labels: Record<string, string> = {
    present: "şimdiki zaman",
    past: "geçmiş zaman",
    perfect: "yakın geçmiş",
    pluperfect: "uzak geçmiş",
    conditional: "şart kipi",
    imperative: "emir kipi",
  };
  return labels[tense] || tense;
}

function getDifficultyColor(level: string): string {
  const colors: Record<string, string> = {
    A1: "bg-green-500/20 text-green-400",
    A2: "bg-green-600/20 text-green-300",
    B1: "bg-yellow-500/20 text-yellow-400",
    B2: "bg-orange-500/20 text-orange-400",
    C1: "bg-red-500/20 text-red-400",
    C2: "bg-red-600/20 text-red-300",
  };
  return colors[level] || "bg-white/10 text-white";
}
